import { postApi } from "utils/apiCall";
import periodicalClient from "./periodicalClient";
import { CLIENT_ID } from "constants/ApiEndPointsConstants";

// module_id "2" matches the mapping in src/components/layout-components/MenuContent.js's
// handleExternalClick, which mints this same handoff token for the old
// window.open flow.
const WORK_SCHEDULER_MODULE_ID = "2";

// Simplest of the three auth flows built so far (see ../cleaningAudit/cleaningAuditAuth.js
// and ../laundryAudit/laundryAuditAuth.js for the other two): this API's own
// /periodical_auto_login is explicitly exempt from its authenticate middleware
// (confirmed by reading MajesticERP-PeridicalAPI/code/start-papi.js's
// exclude_request list directly), and it resolves the acting MyM user from the
// handoff token itself (getUserMymIdFromToken) rather than requiring an
// explicit user-id header the way Laundry Audit's does.
//
// 1. MyM's OWN backend mints a fresh, one-time, module-scoped token via
//    /auto_login_app_web (src/redux/actions/postAutoLogin.action.js).
// 2. That token is exchanged with Periodical's /periodical_auto_login for a
//    Periodical-specific session token, which every further call uses.
let cachedSessionPromise = null;
// Which MyM user/client the cached session belongs to. Sign-out/sign-in
// doesn't reload the page, so without this a manager signing in after an
// admin in the same tab would keep using the admin's module session.
let cachedSessionOwner = null;
const currentSessionOwner = () => `${localStorage.getItem("user_id")}|${localStorage.getItem("client_id")}`;

const getHandoffToken = async () => {
  const userId = localStorage.getItem("user_id");
  const { error, result } = await postApi("/auto_login_app_web", {
    module_id: WORK_SCHEDULER_MODULE_ID,
    user_id: userId,
  });
  const handoffToken = result?.result?.token_id;
  if (error || !handoffToken) {
    throw new Error("Could not obtain a Work Scheduler handoff token from MyM.");
  }
  return handoffToken;
};

const exchangeToken = async () => {
  const handoffToken = await getHandoffToken();
  const clientId = localStorage.getItem(CLIENT_ID);
  const { data } = await periodicalClient.post(
    "/periodical_auto_login",
    { token_id: handoffToken, client_id: clientId },
    {
      headers: {
        "token-id": handoffToken,
        "device-type": process.env.REACT_APP_PERIODICAL_DEVICE_TYPE,
      },
    }
  );
  const sessionTokenId = data?.result?.token_id;
  if (!sessionTokenId) {
    throw new Error("Work Scheduler login did not return a token.");
  }
  return { tokenId: sessionTokenId, clientId };
};

export const getPeriodicalSession = () => {
  if (cachedSessionOwner !== currentSessionOwner()) {
    cachedSessionPromise = null;
    cachedSessionOwner = currentSessionOwner();
  }
  if (!cachedSessionPromise) {
    cachedSessionPromise = exchangeToken().catch((error) => {
      cachedSessionPromise = null; // let the next call retry instead of caching a failure
      throw error;
    });
  }
  return cachedSessionPromise;
};

// Same rationale as the other two modules — force a fresh exchange after a
// 401 rather than assume the user is unauthorized.
export const invalidatePeriodicalSession = () => {
  cachedSessionPromise = null;
};
